// External System API Service
import { AssetMetadata } from '../types';

// Base URL for the external system API
const API_BASE_URL = '/api/external-system';

// External System Paginated Search Response Interface
export interface ExternalSystemPaginatedSearchResponse {
    items: ExternalSystemAssetResponse[];
    current_item_count: number;
    total_items: number;
    page_index: number;
    total_pages: number;
    items_per_page: number;
}

// External System Search Request Interface
export interface ExternalSystemSearchRequest {
    query: string;
    limit?: number;
    offset?: number;
    
    // CRITICAL: Account ID for security filtering - prevents cross-account data leakage.
    // Required by the backend; cross-account search is no longer supported.
    accountId?: string;
    
    // Filter parameters - support both indexed and empty bracket formats
    filter?: {
        mediaType?: string[];
        workspaceId?: string[];
        projectId?: string[];
        accountStatusId?: string[];
        tags?: string[];
        userId?: string[];
        resolution?: {
            minWidth?: number;
            maxWidth?: number;
            minHeight?: number;
            maxHeight?: number;
        };
        duration?: {
            min?: number;
            max?: number;
        };
    };
    
    // Pagination parameters
    pagination?: {
        pageIndex?: number;
        itemsPerPage?: number;
    };
    
    // Search options
    tagSearchMode?: 'AND' | 'OR';
    useSemanticSearch?: boolean;
    useFuzzySearch?: boolean;
    usePrefixMatching?: boolean;
    
    // Tag cloud options
    includeTagCloud?: boolean;
    normalizeTags?: boolean;
    
    // Sorting
    sort?: {
        by?: 'RELEVANCE' | 'CREATED_AT' | 'UPDATED_AT' | 'NAME' | 'SIZE';
        direction?: 'ASC' | 'DESC';
    };
}

// External System Tag Cloud Request Interface
export interface ExternalSystemTagCloudRequest {
    searchText: string;
    tagSearchText?: string;
    tagOrder?: 'POPULARITY' | 'PRIORITY' | 'ALPHABETICAL' | 'COUNT';
    tagSearchMode?: 'AND' | 'OR';
    normalizeTags?: boolean;
    usePrefixMatching?: boolean;
    
    // Account ID filter (required by the backend; cross-account search is
    // no longer supported)
    accountId?: string;
    
    // Filter parameters - same as search
    filter?: {
        mediaType?: string[];
        workspaceId?: string[];
        projectId?: string[];
        accountStatusId?: string[];
        tags?: string[];
        userId?: string[];
        resolution?: {
            minWidth?: number;
            maxWidth?: number;
            minHeight?: number;
            maxHeight?: number;
        };
        duration?: {
            min?: number;
            max?: number;
        };
    };
    
    // Pagination
    pagination?: {
        pageIndex?: number;
        itemsPerPage?: number;
    };
    
    // Sorting
    sort?: {
        by?: 'RELEVANCE' | 'CREATED_AT' | 'UPDATED_AT' | 'NAME' | 'SIZE';
        direction?: 'ASC' | 'DESC';
    };
}

// External System Asset Response Interface
export interface ExternalSystemAssetResponse {
    id: string;
    name: string;
    title: string;
    description: string;
    media_type: string;
    content_type: string;
    mime_type: string;
    asset_type: string;
    url: string;
    sas_url: string;
    thumbnail_url: string;
    tags: string[];
    folder_tags: string[];
    unified_tags: string[];
    workspace_id: string;
    project_id: string;
    account_id: string;
    account_status_id?: number | null;
    created_user_id: string;
    created_at: string;
    updated_at: string;
    size: number;
    file_size: number;
    width: number;
    height: number;
    duration: number;
    resolution: string;
    orientation: string;
    frame_rate: number;
    has_standardized_metadata: boolean;
    metadata: string; // JSON string containing extensive metadata
    comment_count: number | null;
    max_comment_depth: number | null;
    latest_comment_date: string;
    indexed_at: string;
    preview_url: string;
}

// External System Tag Cloud Item Interface
export interface ExternalSystemTagCloudItem {
    id: string;
    value: string;
    count: number;
    percentage: number;
    priority: number;
}

// External System Tag Cloud Response Interface
export interface ExternalSystemTagCloudResponse {
    items: ExternalSystemTagCloudItem[];
    current_item_count: number;
    total_items: number;
    page_index: number;
    total_pages: number;
    items_per_page: number;
}

// Helper function to build filter query parameters
function buildFilterParams(filter: ExternalSystemSearchRequest['filter'] | ExternalSystemTagCloudRequest['filter']): string {
    if (!filter) return '';
    
    const params = new URLSearchParams();
    
    // Handle media type filter
    if (filter.mediaType && filter.mediaType.length > 0) {
        filter.mediaType.forEach((type, index) => {
            params.append(`filter[mediaType][${index}]`, type);
        });
    }
    
    // Handle workspace ID filter
    if (filter.workspaceId && filter.workspaceId.length > 0) {
        filter.workspaceId.forEach((id, index) => {
            params.append(`filter[workspaceId][${index}]`, id);
        });
    }
    
    // Handle project ID filter
    if (filter.projectId && filter.projectId.length > 0) {
        filter.projectId.forEach((id, index) => {
            params.append(`filter[projectId][${index}]`, id);
        });
    }

    // Handle account status ID filter
    if (filter.accountStatusId && filter.accountStatusId.length > 0) {
        filter.accountStatusId.forEach((id, index) => {
            params.append(`filter[accountStatusId][${index}]`, id);
        });
    }
    
    // Handle tags filter
    if (filter.tags && filter.tags.length > 0) {
        filter.tags.forEach((tag, index) => {
            params.append(`filter[tags][${index}]`, tag);
        });
    }
    
    // Handle user ID filter
    if (filter.userId && filter.userId.length > 0) {
        filter.userId.forEach((id, index) => {
            params.append(`filter[userId][${index}]`, id);
        });
    }
    
    // Handle resolution filter
    if (filter.resolution) {
        if (filter.resolution.minWidth !== undefined) {
            params.append('filter[resolution][minWidth]', filter.resolution.minWidth.toString());
        }
        if (filter.resolution.maxWidth !== undefined) {
            params.append('filter[resolution][maxWidth]', filter.resolution.maxWidth.toString());
        }
        if (filter.resolution.minHeight !== undefined) {
            params.append('filter[resolution][minHeight]', filter.resolution.minHeight.toString());
        }
        if (filter.resolution.maxHeight !== undefined) {
            params.append('filter[resolution][maxHeight]', filter.resolution.maxHeight.toString());
        }
    }
    
    // Handle duration filter
    if (filter.duration) {
        if (filter.duration.min !== undefined) {
            params.append('filter[duration][min]', filter.duration.min.toString());
        }
        if (filter.duration.max !== undefined) {
            params.append('filter[duration][max]', filter.duration.max.toString());
        }
    }
    
    return params.toString();
}

// Helper function to build pagination query parameters
function buildPaginationParams(pagination: ExternalSystemSearchRequest['pagination'] | ExternalSystemTagCloudRequest['pagination']): string {
    if (!pagination) return '';
    
    const params = new URLSearchParams();
    
    if (pagination.pageIndex !== undefined) {
        params.append('pagination[pageIndex]', pagination.pageIndex.toString());
    }
    
    if (pagination.itemsPerPage !== undefined) {
        params.append('pagination[itemsPerPage]', pagination.itemsPerPage.toString());
    }
    
    return params.toString();
}

// Helper function to build sort query parameters
function buildSortParams(sort: ExternalSystemSearchRequest['sort'] | ExternalSystemTagCloudRequest['sort']): string {
    if (!sort) return '';
    
    const params = new URLSearchParams();
    
    if (sort.by) {
        params.append('sort[by]', sort.by);
    }
    
    if (sort.direction) {
        params.append('sort[direction]', sort.direction);
    }
    
    return params.toString();
}

// Search assets using external system API
export async function searchAssetsExternalSystem(
    request: ExternalSystemSearchRequest
): Promise<ExternalSystemPaginatedSearchResponse> {
    try {
        const params = new URLSearchParams();
        
        // Add basic search parameters
        params.append('query', request.query);
        
        // CRITICAL: Add account ID parameter for security filtering
        if (request.accountId) {
            params.append('accountId', request.accountId);
        }
        
        if (request.limit !== undefined) {
            params.append('limit', request.limit.toString());
        }
        
        if (request.offset !== undefined) {
            params.append('offset', request.offset.toString());
        }
        
        // Add filter parameters
        const filterParams = buildFilterParams(request.filter);
        if (filterParams) {
            // Parse the filter params string and add each parameter individually
            const filterParamsObj = new URLSearchParams(filterParams);
            Array.from(filterParamsObj.entries()).forEach(([key, value]) => {
                params.append(key, value);
            });
        }
        
        // Add pagination parameters
        const paginationParams = buildPaginationParams(request.pagination);
        if (paginationParams) {
            // Parse the pagination params string and add each parameter individually
            const paginationParamsObj = new URLSearchParams(paginationParams);
            Array.from(paginationParamsObj.entries()).forEach(([key, value]) => {
                params.append(key, value);
            });
        }
        
        // Add sort parameters
        const sortParams = buildSortParams(request.sort);
        if (sortParams) {
            // Parse the sort params string and add each parameter individually
            const sortParamsObj = new URLSearchParams(sortParams);
            Array.from(sortParamsObj.entries()).forEach(([key, value]) => {
                params.append(key, value);
            });
        }
        
        // Add search options
        if (request.tagSearchMode) {
            params.append('tagSearchMode', request.tagSearchMode);
        }
        
        if (request.useSemanticSearch !== undefined) {
            params.append('useSemanticSearch', request.useSemanticSearch.toString());
        }
        
        if (request.useFuzzySearch !== undefined) {
            params.append('useFuzzySearch', request.useFuzzySearch.toString());
        }
        
        if (request.usePrefixMatching !== undefined) {
            params.append('usePrefixMatching', request.usePrefixMatching.toString());
        }
        
        // Add tag cloud options
        if (request.includeTagCloud !== undefined) {
            params.append('includeTagCloud', request.includeTagCloud.toString());
        }
        
        if (request.normalizeTags !== undefined) {
            params.append('normalizeTags', request.normalizeTags.toString());
        }
        
        const response = await fetch(`${API_BASE_URL}/search?${params.toString()}`);
        
        if (!response.ok) {
            throw new Error(`Search failed: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        return data;
        
    } catch (error) {
        console.error('Error searching assets:', error);
        throw error;
    }
}

// Get tag cloud using external system API
export async function getTagCloudExternalSystem(
    request: ExternalSystemTagCloudRequest
): Promise<ExternalSystemTagCloudResponse> {
    try {
        const params = new URLSearchParams();
        
        // Add basic parameters
        params.append('searchText', request.searchText);
        
        if (request.tagSearchText) {
            params.append('tagSearchText', request.tagSearchText);
        }
        
        if (request.tagOrder) {
            params.append('tagOrder', request.tagOrder);
        }
        
        if (request.tagSearchMode) {
            params.append('tagSearchMode', request.tagSearchMode);
        }
        
        if (request.normalizeTags !== undefined) {
            params.append('normalizeTags', request.normalizeTags.toString());
        }

        if (request.usePrefixMatching !== undefined) {
            params.append('usePrefixMatching', request.usePrefixMatching.toString());
        }
        
        // Add account ID parameter
        if (request.accountId) {
            params.append('accountId', request.accountId);
        }
        
        // Add filter parameters
        const filterParams = buildFilterParams(request.filter);
        if (filterParams) {
            // Parse the filter params string and add each parameter individually
            const filterParamsObj = new URLSearchParams(filterParams);
            Array.from(filterParamsObj.entries()).forEach(([key, value]) => {
                params.append(key, value);
            });
        }
        
        // Add pagination parameters
        const paginationParams = buildPaginationParams(request.pagination);
        if (paginationParams) {
            // Parse the pagination params string and add each parameter individually
            const paginationParamsObj = new URLSearchParams(paginationParams);
            Array.from(paginationParamsObj.entries()).forEach(([key, value]) => {
                params.append(key, value);
            });
        }
        
        // Add sort parameters
        const sortParams = buildSortParams(request.sort);
        if (sortParams) {
            // Parse the sort params string and add each parameter individually
            const sortParamsObj = new URLSearchParams(sortParams);
            Array.from(sortParamsObj.entries()).forEach(([key, value]) => {
                params.append(key, value);
            });
        }
        
        const response = await fetch(`${API_BASE_URL}/tag-cloud?${params.toString()}`);
        
        if (!response.ok) {
            throw new Error(`Tag cloud request failed: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        return data;
        
    } catch (error) {
        console.error('Error getting tag cloud:', error);
        throw error;
    }
}

// Convert ExternalSystemAssetResponse to AssetMetadata
export function convertToAssetMetadata(externalAsset: ExternalSystemAssetResponse): AssetMetadata {
    return {
        id: externalAsset.id,
        title: externalAsset.name,
        description: '',
        asset_type: externalAsset.media_type,
        image_url: externalAsset.thumbnail_url,
        sas_url: externalAsset.url,
        file_path: '',
        file_size: externalAsset.size,
        mime_type: '',
        tags: externalAsset.tags,
        folder_tags: [],
        project_id: externalAsset.project_id,
        created_at: externalAsset.created_at,
        updated_at: externalAsset.updated_at,
        indexed_at: externalAsset.indexed_at,
        metadata: {
            workspaceId: externalAsset.workspace_id,
            projectId: externalAsset.project_id,
            createdUserId: '',
            width: externalAsset.width,
            height: externalAsset.height,
            duration: externalAsset.duration,
            fileSize: externalAsset.size,
            createdAt: externalAsset.created_at,
            updatedAt: externalAsset.updated_at
        }
    };
}
